import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useI18N } from "@src/language/language-context";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { updateShowConfigurationInstallModal } from "@src/reducers/your-reducer";
import { abortInstallFirmwareUseCase } from "@src/cloud-connect/ota-update/use-cases/get-abort-install-use-case";
import { getInstallConfigurationStatusUseCase } from "@src/cloud-connect/ota-update/use-cases/get-install-configuration-status-use-case";

interface UseOtaConfigurationInstallModalProps {
  installUpdateId: string | null;
  setInstallUpdateId: React.Dispatch<React.SetStateAction<string | null>>;
}

const MAX_RETRIES = 12;
const POLL_INTERVAL = 1000;

export const useOtaConfigurationInstallModal = ({
  installUpdateId,
  setInstallUpdateId,
}: UseOtaConfigurationInstallModalProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("INITIALIZED");

  const texts = {
    installingConfigurationText: i18n(
      "modules.otaConfigurationInstallModal.installingConfigurationText",
    ),
    installConfigurationOnDeviceText: i18n(
      "modules.otaConfigurationInstallModal.installConfigurationOnDeviceText",
    ),
    descriptionText: i18n(
      "modules.otaConfigurationInstallModal.descriptionText",
    ),
    configurationText: i18n(
      "modules.otaConfigurationInstallModal.configurationText",
    ),
    versionText: i18n("modules.otaConfigurationInstallModal.versionText"),
    digestText: i18n("modules.otaConfigurationInstallModal.digestText"),
    sizeText: i18n("modules.otaConfigurationInstallModal.sizeText"),
    progressText: i18n("modules.otaConfigurationInstallModal.progressText"),
    configurationInstallationCompleted: i18n(
      "modules.otaConfigurationInstallModal.configurationInstallationCompleted",
    ),
    configurationInstallationFailed: i18n(
      "modules.otaConfigurationInstallModal.configurationInstallationFailed",
    ),
    installationTimeoutFailed: i18n(
      "modules.otaConfigurationInstallModal.installationTimeoutFailed",
    ),
  };

  const abortInstallTexts = {
    installAbortedText: i18n("modules.otaAbortInstall.installAbortedText"),
    failedToAbort: i18n("modules.otaAbortInstall.failedToAbort"),
    errorWhileInstallation: i18n(
      "modules.otaAbortInstall.errorWhileInstallation",
    ),
  };

  const closeModal = useCallback(() => {
    dispatch(updateShowConfigurationInstallModal(false));
  }, [dispatch]);

  const handleAbortInstall = useCallback(
    async (updateId: string) => {
      if (["DONE", "FAILED"].includes(status)) return;

      try {
        const result = await abortInstallFirmwareUseCase(updateId);

        if (result.success) {
          setInstallUpdateId(null);
          successSnackbar(abortInstallTexts.installAbortedText, 5000);
        } else {
          errorSnackbar(
            abortInstallTexts.failedToAbort ||
              result.error ||
              abortInstallTexts.errorWhileInstallation,
            5000,
          );
        }
      } catch {
        errorSnackbar(abortInstallTexts.errorWhileInstallation, 5000);
      }
    },
    [setInstallUpdateId, status],
  );

  const closeModalFromXIcon = useCallback(() => {
    if (installUpdateId) {
      handleAbortInstall(installUpdateId);
    }
    dispatch(updateShowConfigurationInstallModal(false));
  }, [dispatch, handleAbortInstall, installUpdateId]);

  useEffect(() => {
    if (!installUpdateId) return;

    let retries = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;

      const result = await getInstallConfigurationStatusUseCase(
        installUpdateId,
      );

      if (result.success) {
        const res = result.data;

        setProgress(res.progress_in_percent);
        setStatus(res.status);

        if (res.status === "DONE") {
          setStatus(res.status);
          setInstallUpdateId(null);
          closeModal();
          successSnackbar(texts.configurationInstallationCompleted, 5000);
          return;
        }

        if (res.status === "FAILED") {
          setStatus(res.status);
          setInstallUpdateId(null);
          closeModal();
          errorSnackbar(texts.configurationInstallationFailed, 5000);
          return;
        }
      } else {
        errorSnackbar(
          result.error || texts.configurationInstallationFailed,
          5000,
        );
        return;
      }

      retries++;

      if (retries >= MAX_RETRIES) {
        errorSnackbar(texts.installationTimeoutFailed, 5000);
        return;
      }

      setTimeout(poll, POLL_INTERVAL);
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [closeModal, installUpdateId, setInstallUpdateId]);

  return {
    progress,
    texts,
    closeModalFromXIcon,
  };
};
